import Immutable from 'immutable';

import type { ActionType } from 'sharedreact/actions/generic/actions';
import SortOrder from 'sharedreact/data/SortOrder';
import AppDispatcher from 'sharedreact/dispatcher/AppDispatcher';
import ItemsState from 'sharedreact/stores/generic/ItemsState';
import PaginatedItemsStore from 'sharedreact/stores/generic/PaginatedItemsStore';

import TokensActions from 'workspaces/launcher/actions/TokensActions';
import TokensConstants from 'workspaces/launcher/constants/TokensConstants';
import Token from 'workspaces/launcher/data/Token';

let _copyData: string = '';

function _canCopyToClipboard (): boolean {
  return (!!navigator.clipboard && window.isSecureContext);
}

function _copyTextToClipboard (text: string, showCopyButton: boolean, showCopyButtonOnError: boolean): void {
  if (!navigator.clipboard) {
    if (!window.isSecureContext) {
      console.log('Copy to clipboard only supported over https.');
    } else {
      console.log('Problem copying token to clipboard.');
    }
    return;
  }
  navigator.clipboard.writeText(text)
    .then(() => {
      if (showCopyButton) {
        _copyData = text;
        TokensActions.showCopyButton();
      } else {
        TokensActions.showClipboardMsg();
      }
    })
    .catch((error1) => {
      if (showCopyButtonOnError) {
        _copyData = text;
        TokensActions.showCopyButton();
      } else {
        console.log('Problem copying token to clipboard.');
      }
    });
}

const ExtraDataContent: {
  showClipboardMsg: boolean,
  needsConfirm: boolean,
  saveMsg: string,
  showCopyButton: boolean
} = {
  showClipboardMsg: false,
  needsConfirm: false,
  saveMsg: '',
  showCopyButton: false
};

class ExtraData extends Immutable.Record(ExtraDataContent) {
}

class TokensStore extends PaginatedItemsStore<Token, ExtraData> {
  constructor () {
    super(AppDispatcher, TokensActions, TokensConstants);
  }

  override getInitialState (): ItemsState<Token | null, ExtraData> {
    return super.getInitialState().set('extraData', new ExtraData());
  }

  getInitialSortOrder (): SortOrder {
    return new SortOrder({keyword: 'user', ascending: true});
  }

  getFilterKeywords (): string[] {
    return [ 'creationdate', 'tags' ]; // 'user', 'grantedby',
  }

  getItemKeyForKeyword (keyword: string): string {
    switch (keyword) {
      case 'user': {
        return 'userName';
      }
      case 'creationdate': {
        return 'creationDate';
      }
      case 'grantedby': {
        return 'grantedBy';
      }
      default: {
        // do nothing
      }
    }
    
    return keyword;
  }

  getShowClipboardMsg (): boolean {
    return this.getState().extraData!.showClipboardMsg;
  }

  getShowCopyButton (): boolean {
    return this.getState().extraData!.showCopyButton;
  }

  canCopyToClipboard (): boolean {
    return _canCopyToClipboard();
  }

  copyTokenToClipboard (): void {
    _copyTextToClipboard(_copyData, false, false);
  }

  getCopyData (): string {
    return _copyData;
  }

  getTotal (): number {
    return this.getState().items.size;
  }
  
  needsConfirm (): boolean {
    return this.getState().extraData!.needsConfirm;
  }

  getSaveMsg (): string {
    return this.getState().extraData!.saveMsg;
  }

  override reduce (state: ItemsState<Token | null, ExtraData>, action: ActionType): ItemsState<Token | null, ExtraData> {
    switch (action.actionType) {
      case TokensConstants.CREATE_SUCCESS: {
        if (_canCopyToClipboard()) {
          _copyTextToClipboard(action.tokenString, false, true);
        } else {
          _copyData = action.tokenString;
          setTimeout(() => TokensActions.showCopyButton());
        }

        this._getRange(
          state,
          this._currentRange,
          state.filter,
          state.advancedFilter,
          state.sortOrder
        );

        return state;
      }
      case TokensConstants.SHOW_CLIPBOARDMSG: {
        state = state.set('extraData', state.extraData!.set('showClipboardMsg', true));
        return state;
      }
      case TokensConstants.SHOW_COPY_BUTTON: {
        if (action.show) {
          state = state.set('extraData', state.extraData!.set('showCopyButton', true));
        } else {
          state = state.set('extraData', state.extraData!.set('showCopyButton', false));
        }
        return state;
      }
      case TokensConstants.HIDE_CLIPBOARDMSG: {
        state = state.set('extraData', state.extraData!.set('showClipboardMsg', false));
        return state;
      }
      case TokensConstants.NEEDS_CONFIRM: {
        state = state.set('extraData', state.extraData!.set('needsConfirm', action.needsConfirm));
        return state;
      }
      case TokensConstants.UPDATE_SUCCESS: {
        state = state.set('extraData', state.extraData!.set('saveMsg', 'launcher.admin.tokens.saved'));
        this._getRange(
          state,
          this._currentRange,
          state.filter,
          state.advancedFilter,
          state.sortOrder
        );
        return state;
      }
      case TokensConstants.UPDATE_FAIL: {
        state = state.set('extraData', state.extraData!.set('saveMsg', 'launcher.admin.tokens.savefailed'));
        return state;
      }
      case TokensConstants.CLOSE_DETAILS: {
        state = state.set('extraData', state.extraData!.set('saveMsg', ''));
        return state;
      }
      default: {
        // do nothing
        break;
      }
    }

    return super.reduce(state, action);
  }
}

export default new TokensStore();
